import { Component, OnInit } from '@angular/core';
import { AbstractWidgetDirective, IWidgetComponentDefinition } from '@azavista/components/widgets';
import { IEmailCampaignDeliverySettings } from './interfaces';
import {
    AzavistaApiService,
    EmailCampaignDeliveryStatus,
    IEmailCampaign,
    IEvent,
    IGetReportRequest,
    IGetReportResponse,
} from '@azavista/servicelib';
import { CommonDialogsService } from '../../common-dialogs.service';
import { TranslationKey } from '../../enums';
import { SharedService } from '../../shared.service';
import { IWidgetDataChanges, WidgetViewDetailsNavigationPaths } from '../interfaces';
import { WidgetsUtilsService } from '../widgets-utils.service';
import {
    TRANSLATION_MAP,
    TranslationMapKey,
} from '../../const';
import { IDataPointSelectedArguments } from '../apexcharts-wrapper/interfaces';
import { WidgetCustomEventType } from '../enums';
import { IField } from '@azavista/components/shared';
import { Subject, delay, takeUntil } from 'rxjs';
import { ChartType } from '../apextypes';

interface IResponseType {
    [key: string]: number;
}

type SettingsRemoteFields = Record<
    keyof Omit<IEmailCampaignDeliverySettings['remote'], 'event_id'>,
    IField
>;

type SettingsLocalFields = Record<
    keyof IEmailCampaignDeliverySettings['local'],
    IField
>;

const EMAIL_CAMPAIGN_DELIVERY_STATUS_TRANSLATIONS: Record<
    EmailCampaignDeliveryStatus,
    TranslationMapKey
> = {
    [EmailCampaignDeliveryStatus.sent]: TRANSLATION_MAP.SENT,
    [EmailCampaignDeliveryStatus.opened]: TRANSLATION_MAP.OPENED,
    [EmailCampaignDeliveryStatus.link_clicked]: TRANSLATION_MAP.LINK_CLICKED,
    [EmailCampaignDeliveryStatus.bounced]: TRANSLATION_MAP.BOUNCED,
    [EmailCampaignDeliveryStatus.unsubscribed]: TRANSLATION_MAP.UNSUBSCRIBED,
};

const CHART_TYPE_TRANSLATIONS: Record<'bar' | 'donut', TranslationMapKey> = {
    bar: TRANSLATION_MAP.CHART_TYPE_BAR,
    donut: TRANSLATION_MAP.CHART_TYPE_DONUT,
};

@Component({
    selector: 'azavista3-email-campaign-delivery',
    templateUrl: './email-campaign-delivery.component.html',
    standalone: false
})
export class EmailCampaignDeliveryWidgetComponent
    extends AbstractWidgetDirective
    implements OnInit
{
    chart: ApexChart = {
        type: 'donut',
    };
    isEventPredefined = false;
    eventNotAvailable = false;
    translations = TRANSLATION_MAP;
    private destroyed$ = new Subject<void>();

    eventId?: string;
    event?: IEvent;
    definitionSettings?: IEmailCampaignDeliverySettings;
    emailCampaigns?: IEmailCampaign[];
    series?: ApexAxisChartSeries | ApexNonAxisChartSeries;
    labels?: string[];
    settingsRemoteFields?: SettingsRemoteFields;
    settingsLocalFields: SettingsLocalFields = this.getSettingsLocalFields();

    constructor(
        private apiSvc: AzavistaApiService,
        private sharedSvc: SharedService,
        private widgetsUtilsSvc: WidgetsUtilsService,
        private commonDlgsSvc: CommonDialogsService
    ) {
        super();
    }

    async ngOnInit() {
        this.definitionSettings = this.definition.settings;
        this.definition.data = null;

        this.eventId = this.definition.context?.eventId;
        try {
            if (this.eventId) {
                // The widget is inside event context
                await this.setEventFromContext(this.eventId);
                this.eventNotAvailable = false;
            } else {
                this.eventId = this.definition.settings.remote.event_id;
            }
        } catch (err) {
            this.eventNotAvailable = true;
            return;
        }
        this.setCustomWidgetParams();
        await this.initFields();

        if (this.configMode) {
            if (this.eventId) {
                // Initially add only the selected event as item in event autocomplete
                try {
                    this.event ||= await this.widgetsUtilsSvc.getEvent(this.eventId);
                    this.eventNotAvailable = false;
                } catch (err) {
                    this.eventNotAvailable = true;
                }
            }
            this.onSettingsChanged();
        } else {
            const areRemoteSettingsProvded = this.isSettingsValid(this.definitionSettings);
            if (areRemoteSettingsProvded) {
                // Start loading on next loop because it causes loading output to be emitted which could cause
                // Angular change detection errors in the consumer (the dashboard component) if it changes template bound variables
                // in the event handler
                await delay(0);
                this.loadWidgetData();
            } else {
                // Notify parent that we need remote settings
                this.settingsNeeded.next();
            }
        }

        this.chart = {
            ...this.chart,
            type: this.definitionSettings && this.definitionSettings.local.chartType
                ? this.definitionSettings.local.chartType
                : this.chart.type,
            toolbar: { show: false },
            zoom: { enabled: false },
            animations: { enabled: true },
        };

        this.definitionSettings.local.chartType ??= this.chart.type as any;
        const { series, labels } = this.convertDataToSeriesAndLabels(this.definition.data);
        this.series = this.widgetsUtilsSvc.convertSeries(this.chart.type, series);
        this.labels = labels;

        super.subscribeToDataChanges({
            next: (newData?: IWidgetDataChanges) => {
                this.dataChanged(newData);
            }
        });
    }

    loadWidgetData(): void {
        this.definitionSettings = this.definition.settings;
        const definition = this.definition;
        this.eventNotAvailable = !this.definitionSettings?.remote?.event_id;
        if (definition.size?.height) {
            this.chart.height = definition.size.height;
            this.chart.width = definition.size.width;
        }
        const req: IGetReportRequest<'emailcampaigndelivery'> = {
            reportType: 'emailcampaigndelivery',
            widgetId: definition.id,
            payload: this.definitionSettings.remote
        };
        this.notifyLoading(true);
        this.apiSvc.getReport(req)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: response => this.processDataLoaded(definition, response),
                error: () => this.processDataLoadedError()
            });
    }

    async setEventFromContext(contextEventId: string): Promise<void> {
        this.isEventPredefined = true;
        this.definitionSettings.remote.event_id = contextEventId;
        this.event = await this.widgetsUtilsSvc.getEvent(contextEventId);
        this.eventId = this.event.id;
    }

    setCustomWidgetParams(): void {
        this.definition.viewButtonText = this.sharedSvc.translate(
            TranslationKey.goToEvent
        );
        this.definition.viewDetailsNavPath =
            WidgetViewDetailsNavigationPaths.event;
        this.definition.viewDetailsNavQueryParams =
            this.widgetsUtilsSvc.getViewDetailsNavQueryParamsForEvent(
                this.eventId
            );
        this.onSettingsChanged();
    }

    onSettingsChanged(): void {
        const settingsAreValid = this.isSettingsValid(this.definitionSettings);
        this.settingsChanged.next({
            definition: this.definition,
            settings: this.definitionSettings,
            settingsAreValid: settingsAreValid,
        });
    }

    async onSelectEvent(): Promise<void> {
        const title = this.sharedSvc.translate(TranslationKey.events);
        const dlgResult = await this.commonDlgsSvc.showSelectEventsDialog(
            title,
            true
        );
        if (!dlgResult || dlgResult.length === 0) {
            return;
        }
        this.event = dlgResult[0] as IEvent;
        const eventId = this.event ? this.event.id : null;
        this.definitionSettings.remote.event_id = eventId;
        this.eventId = eventId;
        this.setCustomWidgetParams();
        this.onSettingsChanged();
        await this.initFields();
    }

    onDataPointSelected(args: IDataPointSelectedArguments): void {
        this.customEvent.emit({
            type: WidgetCustomEventType.dataPointSelected,
            data: args,
        });
    }

    async setEmailCampaigns(eventId: string): Promise<void> {
        this.emailCampaigns = eventId
            ? await this.apiSvc.getAllEventEmailCampaigns(eventId)
            : [];
    }

    isSettingsValid(settings: IEmailCampaignDeliverySettings) {
        const { local, remote } = settings;
        return (
            !!remote.email_campaign_id &&
            !!(remote.email_statuses?.length > 0) &&
            !!remote.event_id
        );
    }

    getEmailCampaign(emailCampaignId: string | undefined) {
        return (
            emailCampaignId &&
            this.emailCampaigns?.find(
                (campaign) => campaign.id === emailCampaignId
            )
        );
    }

    private async initFields() {
        if (this.eventId) {
            await this.setEmailCampaigns(this.eventId);
            this.settingsRemoteFields = this.getSettingsRemoteFields();
        }
    }

    private getSettingsRemoteFields(): SettingsRemoteFields {
        return {
            email_campaign_id: this.sharedSvc.createSelectField(
                {
                    label: this.sharedSvc.translate(
                        TRANSLATION_MAP.EMAIL_CAMPAIGN
                    ),
                    name: 'email_campaign_id',
                    mandatory_for_planners: true,
                    placeholder: this.sharedSvc.translate(
                        TRANSLATION_MAP.SELECT_EMAIL_CAMPAIGN
                    ),
                },
                this.emailCampaigns?.reduce((record, campaign) => {
                    record[campaign.id!] = campaign.name;
                    return record;
                }, {} as Record<string, string>) as any
            ),
            email_statuses: {
                ...this.sharedSvc.createMultiSelectFieldValueWithAttributeTranslations(
                    Object.entries(
                        EMAIL_CAMPAIGN_DELIVERY_STATUS_TRANSLATIONS
                    ).map(([key, value]) => ({
                        id: key,
                        name: this.sharedSvc.translate(value),
                    })),
                    'email_statuses',
                    TRANSLATION_MAP.DISPLAY_EMAIL_STATUS,
                    'name'
                ),
                mandatory_for_planners: true,
            },
        };
    }

    private getSettingsLocalFields(): SettingsLocalFields {
        return {
            chartType: this.sharedSvc.createSelectField(
                {
                    label: this.sharedSvc.translate(TRANSLATION_MAP.CHART_TYPE),
                    name: 'chartType',
                },
                CHART_TYPE_TRANSLATIONS
            ),
        };
    }

    dataChanged(newData?: IWidgetDataChanges): void {
        let chartTypeChanged = false;
        let shouldConvertSeries = false;

        if (newData.settings && newData.settings.local) {
            if (this.chart.type !== newData.settings.local.chartType) {
                this.chart.type = newData.settings.local.chartType;
                chartTypeChanged = true;
            }
        }
        if (newData.data) {
            shouldConvertSeries = true;
        }
        if (newData.width && this.chart.width !== newData.width) {
            this.chart.width = newData.width;
        }
        if (newData.height && this.chart.height !== newData.height) {
            this.chart.height = newData.height;
        }
        if (chartTypeChanged || shouldConvertSeries) {
            const { series, labels } = this.convertDataToSeriesAndLabels(newData.data);
            this.series = this.widgetsUtilsSvc.convertSeries(this.chart.type, series);
            this.labels = labels;
        }
        if (chartTypeChanged) {
            this.chartTypeChanged(newData.settings.local.chartType);
        }
        if (!this.configMode && newData.settings && newData.settings.remote) {
            // Not config mode and remote settings were changed - reload data from server
            this.loadWidgetData();
        }
    }

    convertDataToSeriesAndLabels(data: IResponseType): { series: number[], labels: string[] } {
        if (!data) {
            return {
                labels: [],
                series: []
            };
        }
        const series: number[] = [];
        const labels: string[] = [];
        const keys = Object.keys(data);
        for (const key of keys) {
            series.push(data[key]);
            const statusTranslation = EMAIL_CAMPAIGN_DELIVERY_STATUS_TRANSLATIONS[key as EmailCampaignDeliveryStatus];
            labels.push(this.sharedSvc.translate(statusTranslation));
        }

        return {
            series: series,
            labels: labels
        };
    }

    async processDataLoaded(definition: IWidgetComponentDefinition, response: IGetReportResponse): Promise<void> {
        this.notifyLoading(this.sharedSvc.getLoadingOnReportLoaded(response));
        definition.data = response.data;
        definition.dataChangedSubject.next({ data: definition.data });
    }

    processDataLoadedError(): void {
        // TODO: What if report data fails ?
        this.notifyLoading(false);
    }

    chartTypeChanged(newType: string): void {
        this.chart.type = newType as ChartType;
        const { series, labels } = this.convertDataToSeriesAndLabels(this.definition.data);
        this.labels = labels;
        this.series = this.widgetsUtilsSvc.convertSeries(this.chart.type, series);
    }

    onSettingsLocalUpdate<
        K extends keyof IEmailCampaignDeliverySettings['local']
    >(attribute: K, value: IEmailCampaignDeliverySettings['local'][K]) {
        this.definitionSettings.local[attribute] = value;
        this.onSettingsChanged();
    }

    onSettingsRemoteUpdate<
        K extends keyof IEmailCampaignDeliverySettings['remote']
    >(attribute: K, value: IEmailCampaignDeliverySettings['remote'][K]) {
        this.definitionSettings.remote[attribute] = value;
        this.onSettingsChanged();
    }
}
